import React, { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useLanguage } from '@/contexts/LanguageContext';
import { translations } from '@/utils/translations';
import { updateMetaTags, getCanonicalUrl } from '@/utils/seoUtils';
import { blogPosts } from '@/data/blogPosts';
import { blogArticles, getAllArticles } from '@/data/blog';

const Blog = () => {
  const { lang } = useParams<{ lang: string }>();
  const { currentLanguage } = useLanguage();
  const t = translations[currentLanguage];
  const [videoError, setVideoError] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Update SEO meta tags
  useEffect(() => {
    updateMetaTags({
      title: `${t.blog} | Future Level`,
      description: t.blogMetaDescription || 'Erfahren Sie mehr über KI-Videomarketing, Produktvideos und Social Media Strategien in unserem Blog.',
      keywords: "KI Video Marketing Blog, AI Videoproduktion Tipps, Video Content Strategie, Social Media Video Trends",
      canonicalUrl: getCanonicalUrl('blog', currentLanguage),
      language: currentLanguage,
      ogType: "website"
    });
  }, [currentLanguage, t]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      // Event listeners for video loading and errors
      const handleError = () => {
        console.error("Blog page background video error");
        setVideoError(true);
      };
      
      const handleCanPlay = () => {
        console.log("Blog page video can play now");
        setVideoLoaded(true);
        
        // Try to play the video
        try {
          video.play().catch(e => {
            console.warn("Auto-play prevented:", e);
            // We don't set error here as the video can still be displayed
          });
        } catch (e) {
          console.warn("Play attempt error:", e);
        }
      };

      video.addEventListener('error', handleError);
      video.addEventListener('canplay', handleCanPlay);
      
      // Force load the video
      video.load();
      
      return () => {
        video.removeEventListener('error', handleError);
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, []);

  // Add enhanced debugging logs
  console.log('Blog articles from blog index:', Object.keys(blogArticles));
  console.log('All articles from getAllArticles():', getAllArticles().map(article => article.slug));
  console.log('New article available:', blogArticles['ki-video-individuell-markenkonform'] ? 'Yes' : 'No');

  // Combine blog posts from both sources
  const allPosts = [
    // First include the full blog articles
    ...getAllArticles().map(article => {
      console.log('Processing article:', article.slug);
      return {
        slug: article.slug,
        date: article.date,
        image: article.image,
        title: {
          de: article.meta.de.title,
          en: article.meta.en.title,
          fr: article.meta.fr.title,
          it: article.meta.it.title,
        },
        excerpt: {
          de: article.meta.de.description,
          en: article.meta.en.description,
          fr: article.meta.fr.description,
          it: article.meta.it.description,
        }
      };
    }),
    // Then include regular blog posts
    ...blogPosts
  ];

  console.log('Combined posts count:', allPosts.length);
  console.log('Current language:', currentLanguage);

  // Sort posts by date (newest first)
  allPosts.sort((a, b) => {
    const dateA = new Date(a.date.split('.').reverse().join('-'));
    const dateB = new Date(b.date.split('.').reverse().join('-'));
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <div className="flex flex-col min-h-screen bg-[#121212]"> {/* Changed to match homepage dark background */}
      <Header />
      <main className="flex-grow py-16 md:py-24 relative">
        {/* Video background with improved implementation */}
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]"> {/* Darker gradient matching homepage */}
          {!videoError ? (
            <video 
              ref={videoRef}
              className="h-full w-full object-cover opacity-0 transition-opacity duration-700"
              style={{ opacity: videoLoaded ? 0.15 : 0 }} 
              autoPlay 
              muted 
              loop 
              playsInline
              onError={() => setVideoError(true)}
            >
              <source 
                src="https://vdl.prod.dploy.me/veed-transcoder-outputs/cfd6b3af-164e-4c60-bdb1-32c25fdb9fb7/file.mp4" 
                type="video/mp4"
              />
              {/* Fallback source if first one fails */}
              <source 
                src="https://res.cloudinary.com/dndcmrxix/video/upload/v1744503284/veed-blog-background_eswq0q.mp4" 
                type="video/mp4"
              />
            </video>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-r from-[#1A1F2C] to-[#2D3748]"></div>
          )}
          
          {/* Neural pattern overlay like homepage */}
          <div className="absolute inset-0 neural-pattern z-0 opacity-10"></div>
          
          {/* Subtle overlay for readability */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm z-0"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">{t.blogPageTitle}</h1>
            <p className="text-lg text-[#D6BCFA] max-w-3xl mx-auto">
              {t.blogPageSubtitle}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {allPosts.map((post, index) => (
              <article 
                key={index} 
                className="glass-card rounded-xl overflow-hidden transition-transform duration-300 hover:translate-y-[-4px]"
              >
                <Link to={`/${currentLanguage}/blog/${post.slug}`}>
                  <img 
                    src={post.image} 
                    alt={post.title[currentLanguage]} 
                    className="w-full h-56 object-cover"
                  />
                </Link>
                <div className="p-6">
                  <span className="text-sm text-purple-300 mb-2 block">{post.date}</span>
                  <h2 className="text-xl font-semibold mb-3">
                    <Link to={`/${currentLanguage}/blog/${post.slug}`} className="text-white hover:text-purple-300 transition-colors">
                      {post.title[currentLanguage]}
                    </Link>
                  </h2>
                  <p className="text-gray-300 mb-4">
                    {post.excerpt[currentLanguage]}
                  </p>
                  <Link 
                    to={`/${currentLanguage}/blog/${post.slug}`} 
                    className="text-purple-400 hover:text-purple-300 font-medium flex items-center"
                  >
                    {t.readMore}
                    <svg 
                      xmlns="http://www.w3.org/2000/svg" 
                      fill="none" 
                      viewBox="0 0 24 24" 
                      stroke="currentColor" 
                      className="w-4 h-4 ml-1"
                    >
                      <path 
                        strokeLinecap="round" 
                        strokeLinejoin="round" 
                        strokeWidth={2} 
                        d="M14 5l7 7m0 0l-7 7m7-7H3" 
                      />
                    </svg>
                  </Link>
                </div>
              </article>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
